import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import { UrlParamKeys } from '@/common/utils/constants';
import { FetchFilterValuesBasedOnCategoryIdReturnValue } from './fetchFilterValuesBasedOnCategoryId';
import Filter from '../Filter';
import FilterCheckbox from '../components/FilterCheckboxList/FilterCheckbox';
import FilterCheckboxList from '../components/FilterCheckboxList/FilterCheckboxList';
import PriceSlider from '../components/PriceSlider/PriceSlider';
import { FilterVariant } from '@/widgets/FindAClass/classfinder.interfaces';

interface UseFilterProps {
  variant: FilterVariant;
  filterTranslations: Record<string, string>;
  totalAmountOfFilteredItems: number;
  filters: FetchFilterValuesBasedOnCategoryIdReturnValue[];
  selectedFilters: string[];
  setSelectedFilters: (filters: string[]) => void;
  clubsData?: Array<{ startingFromPrice?: number | null }>;
  priceRange?: number[];
  setPriceRange?: (range: number[]) => void;
}

export default function useFilter({
  variant,
  filterTranslations,
  totalAmountOfFilteredItems,
  filters,
  selectedFilters,
  setSelectedFilters,
  clubsData = [],
  priceRange,
  setPriceRange,
}: UseFilterProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const filterParam = searchParams.get(UrlParamKeys.Filter);
  const filterParamArray = filterParam
    ? [...new Set(filterParam.split(','))]
    : undefined;

  useEffect(() => {
    if (filterParamArray) {
      setSelectedFilters(filterParamArray);
      updateUrlWithFilters(filterParamArray);
    }
  }, []);

  const updateUrlWithFilters = (filters: string[]) => {
    const params = new URLSearchParams();

    searchParams.forEach((value, key) => {
      if (key !== UrlParamKeys.Filter) {
        params.set(key, value);
      }
    });

    if (filters.length > 0) {
      const uniqueFilters = [...new Set(filters)];
      params.set(UrlParamKeys.Filter, uniqueFilters.join(','));
    }

    const queryString = params.toString().replace(/%2C/g, ',');
    router.push(`?${queryString}`, { scroll: false });
  };

  const { minPrice, maxPrice } = (() => {
    if (variant !== FilterVariant.Clubs || !clubsData.length)
      return { minPrice: 0, maxPrice: 0 };

    const prices = clubsData
      .map((club) => club.startingFromPrice || 0)
      .filter((price) => price > 0);

    if (!prices.length) return { minPrice: 0, maxPrice: 0 };

    const lowest = Math.floor(Math.min(...prices));
    const highest = Math.ceil(Math.max(...prices));
    return { minPrice: lowest, maxPrice: highest };
  })();

  const [localPriceRange, setLocalPriceRange] = useState<number[]>([
    minPrice,
    maxPrice,
  ]);

  useEffect(() => {
    if (!priceRange && maxPrice > 0) {
      setLocalPriceRange([minPrice, maxPrice]);
    }
  }, [maxPrice, minPrice, priceRange]);

  const currentPriceRange = priceRange || localPriceRange;
  const handlePriceChange = setPriceRange || setLocalPriceRange;

  const filterComponent = (
    <Filter
      filterCount={selectedFilters.length}
      translations={filterTranslations}
      onClearFilters={() => {
        setSelectedFilters([]);
        updateUrlWithFilters([]);
        if (maxPrice > 0) {
          handlePriceChange([minPrice, maxPrice]);
        }
      }}
      totalAmountOfFilteredItems={totalAmountOfFilteredItems}
      variant={variant}
    >
      {variant === FilterVariant.Clubs && maxPrice > 0 ? (
        <>
          <PriceSlider
            key="price-slider"
            minPrice={minPrice}
            maxPrice={maxPrice}
            value={currentPriceRange}
            onChange={handlePriceChange}
            translations={filterTranslations}
          />
          {filters.map((filter, index) => (
            <FilterCheckboxList
              key={filter.category.sys.id}
              title={filter.category.filterTitle?.value || ''}
              isLast={index === filters.length - 1}
            >
              {filter.filterValues.map((filterValue) => (
                <FilterCheckbox
                  key={filterValue.sys.id}
                  filterValue={filterValue}
                  onFilterSelect={() => {
                    const filterValueLower =
                      filterValue.filterValueIdentifier?.toLowerCase() ??
                      filterValue.filterValueTitle?.value?.toLowerCase() ??
                      '';
                    const newFilters = selectedFilters.includes(
                      filterValueLower
                    )
                      ? selectedFilters.filter((f) => f !== filterValueLower)
                      : [...selectedFilters, filterValueLower];

                    setSelectedFilters(newFilters);
                    updateUrlWithFilters(newFilters);
                  }}
                  checked={selectedFilters.includes(
                    filterValue.filterValueIdentifier?.toLowerCase() ??
                    filterValue.filterValueTitle?.value?.toLowerCase() ??
                    ''
                  )}
                />
              ))}
            </FilterCheckboxList>
          ))}
        </>
      ) : (
        filters.map((filter) => (
          <FilterCheckboxList
            key={filter.category.sys.id}
            title={filter.category.filterTitle?.value || ''}
          >
            {filter.filterValues.map((filterValue) => {
              return (
                <FilterCheckbox
                  key={filterValue.sys.id}
                  filterValue={filterValue}
                  onFilterSelect={() => {
                    const filterValueLower =
                      filterValue.filterValueIdentifier?.toLowerCase() ??
                      filterValue.filterValueTitle?.value?.toLowerCase() ??
                      '';
                    const newFilters = selectedFilters.includes(
                      filterValueLower
                    )
                      ? selectedFilters.filter((f) => f !== filterValueLower)
                      : [...selectedFilters, filterValueLower];

                    setSelectedFilters(newFilters);
                    updateUrlWithFilters(newFilters);
                  }}
                  checked={selectedFilters.includes(
                    filterValue.filterValueIdentifier?.toLowerCase() ??
                    filterValue.filterValueTitle?.value?.toLowerCase() ??
                    ''
                  )}
                />
              );
            })}
          </FilterCheckboxList>
        ))
      )}
    </Filter>
  );

  return { filterComponent };
}
